'use client'

import dayjs from 'dayjs'

import { Stock } from '../../icons'
import { formatNumber } from '../../utils/formatNumber'
import { Badge } from '../Badge'
import { Button } from '../Button'
import { Typography } from '../Typography'
import { Container, HeaderItem, HeaderRow, TimetableRow } from './styles'

export interface Timetable {
  id: number
  programId: number
  programName: string
  programDuration: number
  programActivities: (string | { name: string })[]
  programLevel: 1 | 2 | 3 | 4
  gender: 'men' | 'women' | 'none'
  instructor?: string
  startAt: string
  point: number
  stock: number
  reservationDeadline: string
  isApprovable: boolean
  reservationRequesting: boolean
  reserved: boolean
}

export interface TimetableListProps {
  /**
   * List of timetables
   */
  timetables: Timetable[]

  /**
   * Threshold of stock to show low stock icon
   */
  stockLowThreshold?: number

  /**
   * If the user can request reservation
   */
  canRequestReservation?: boolean

  /**
   * If the target timetable is in loading state
   */
  loadingTimetableId?: number

  /**
   * Callback when a timetable is selected
   */
  onSelect?: (timetable: Timetable) => void
}

export const TimetableList = ({
  timetables,
  stockLowThreshold = 5,
  canRequestReservation,
  loadingTimetableId,
  onSelect,
}: TimetableListProps) => {
  return (
    <Container>
      <HeaderRow gridTemplateColumns="80px 1fr">
        <HeaderItem>空席情報</HeaderItem>
        <HeaderItem>プログラム情報</HeaderItem>
      </HeaderRow>

      {timetables.map((timetable) => {
        const isClosed = dayjs().isAfter(dayjs(timetable.reservationDeadline))

        const stockStatus = timetable.isApprovable
          ? 'available'
          : isClosed || timetable.stock <= 0
          ? 'unavailable'
          : timetable.stock <= stockLowThreshold
          ? 'low'
          : 'available'

        const canReserve = isClosed
          ? false
          : timetable.isApprovable
          ? canRequestReservation && !timetable.reservationRequesting
          : !timetable.reserved && stockStatus !== 'unavailable'

        return (
          <TimetableRow
            key={timetable.id}
            gridTemplateColumns="80px 1fr"
            gap={4}
          >
            <div className="flex flex-col items-center justify-center">
              <Typography variant="subhead1">
                {dayjs(timetable.startAt).format('HH:mm')}
              </Typography>

              <Typography variant="body2" color="gray.02">
                {timetable.programDuration}min
              </Typography>

              <div className="mt-2 flex">
                {stockStatus === 'unavailable' && (
                  <Stock variant="unavailable" />
                )}
                {stockStatus === 'available' && <Stock variant="available" />}
                {stockStatus === 'low' && (
                  <Badge variant="outlined">残{timetable.stock}席</Badge>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-3 pr-4">
                <div className="flex flex-col">
                  {timetable.isApprovable && (
                    <div className="mb-2 flex">
                      <Badge variant="outlined-round">仮予約</Badge>
                    </div>
                  )}

                  <Typography variant="subhead1">
                    {timetable.programName}
                  </Typography>

                  {timetable.instructor && (
                    <Typography variant="body2" color="gray.02">
                      (Instr. {timetable.instructor})
                    </Typography>
                  )}
                </div>

                <div className="flex">
                  <Typography
                    variant="subhead1"
                    textAlign="right"
                    color="red.01"
                  >
                    {formatNumber(timetable.point)}pt
                  </Typography>
                </div>
              </div>

              <div className="mt-auto flex items-center justify-between gap-3 pr-4 pt-2">
                <div className="flex flex-wrap gap-2">
                  {timetable.programActivities.map((activity) => {
                    const activityName =
                      typeof activity === 'string' ? activity : activity.name
                    return <Badge key={activityName}>{activityName}</Badge>
                  })}
                  {timetable.gender === 'men' && (
                    <Badge variant="men">男性専用</Badge>
                  )}
                  {timetable.gender === 'women' && (
                    <Badge variant="women">女性専用</Badge>
                  )}
                </div>

                <div className="flex shrink-0 flex-col">
                  <Button
                    variant="brand"
                    onClick={() => onSelect?.(timetable)}
                    loading={timetable.id === loadingTimetableId}
                    disabled={!canReserve}
                  >
                    {timetable.reserved
                      ? '予約済'
                      : timetable.isApprovable
                      ? !timetable.reservationRequesting
                        ? '仮予約する'
                        : '仮予約済み'
                      : '予約する'}
                  </Button>
                </div>
              </div>
            </div>
          </TimetableRow>
        )
      })}
    </Container>
  )
}
